import React, { useState } from 'react';
import './ProcessEventTable.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import Icon from '../home/Icon';
import Tooltip from '../tooltip/Tooltip';
import ActionButton from '../input/ActionButton';
import CustomTogglebox from '../input/CustomTogglebox';
import EventStatus from '../status/EventStatus';
import { ParticipantGroupChip } from '../participant-group/ParticipantGroupChips';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import { compareParticipantGroups, releaseSummary } from '../../util/participantGroups';
import {
  apiErrorMessage, eventNouns, eventShortLabel, formatDate, formatTime, isCancelledState, isOpenEvent,
  normalizeStepKind, releaseLabel, STEP_KIND, stepIcon,
} from './processUtils';

const REMOVE_POPUP = 'processremoveevent';

/**
 * Events of a process: date/time/location, state, release chips per group, leaders (warning
 * "Leitung fehlt" on the row), coverage per mail step (incl. "Nachtrag"), confirmed/max and
 * removing an event (with the option "Freigaben zurücknehmen" and the hint that the process add-ons are no
 * longer bookable there). Table on wide containers, one card per event on narrow ones (container query).
 */
function ProcessEventTable({ process, groupsById, readOnly, onAdd, onNavigateStep, onProcess, reportError }) {
  const { openPopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const [removeEventId, setRemoveEventId] = useState(null);
  const [revokeOnRemove, setRevokeOnRemove] = useState(false);

  const nouns = eventNouns(process);
  const events = process.events ?? [];
  const steps = process.steps ?? [];
  const mailSteps = steps.filter(s => normalizeStepKind(s.kind) === STEP_KIND.SendMail);
  const leaderStep = steps.find(s => normalizeStepKind(s.kind) === STEP_KIND.AssignLeaders) ?? null;
  const canNavigate = !readOnly && typeof onNavigateStep === 'function';
  const removeEvent = events.find(e => e.id === removeEventId) ?? null;
  // Detaching keeps bookings and prices, but the event loses the sign-up add-ons of the process (R10b).
  const addOnNames = (process.addOns ?? []).map(a => a.addOnName).filter(Boolean).join(', ');

  const openRemove = (evt) => {
    setRemoveEventId(evt.id);
    // Default as before: never-public events lose their process releases (members would otherwise
    // keep seeing a private event without its inviting process).
    setRevokeOnRemove(!!process.neverPublic);
    openPopup(REMOVE_POPUP, evt.id);
  };

  const handleRemove = () => {
    if (!removeEventId) return;
    startSuccessBarLoading(`${nouns.one} wird entfernt`);
    api.delete(`/EventProcess/${process.id}/Events/${removeEventId}?revokeReleases=${revokeOnRemove}`)
      .then(response => {
        startSuccessBarSuccess(`${nouns.one} entfernt`);
        onProcess(response.data, { resetView: false });
      })
      .catch(err => {
        closeSuccessBar();
        reportError(apiErrorMessage(err, 'Entfernen fehlgeschlagen.'));
      });
  };

  const renderReleases = (evt) => {
    const groups = (evt.releasedParticipantGroupIds ?? [])
      .map(id => groupsById.get(Number(id)))
      .filter(Boolean)
      .sort(compareParticipantGroups);
    if (groups.length === 0) return <span className={'processEventMuted'}>Nicht freigegeben</span>;
    return (
      <span className={'processEventChips'}>
        {groups.map(g => <ParticipantGroupChip key={g.id} group={g} label={releaseLabel(g)} />)}
      </span>
    );
  };

  const renderLeaders = (evt) => {
    const leaders = evt.leaders ?? [];
    if (leaders.length > 0) {
      const names = leaders.map(l => l.name).join(', ');
      return (
        <Tooltip text={names}>
          <span className={'processEventLeaders'}>
            <span className={'material-symbols-rounded processEventLeaderIcon'} aria-hidden={'true'}>sports</span>
            <span className={'processEventLeaderNames'}>{names}</span>
          </span>
        </Tooltip>
      );
    }
    if (process.requiresLeader && isOpenEvent(evt)) {
      const hint = 'Ohne Spieltagleitung bleibt das Event unsichtbar (Entwurf), auch wenn es freigegeben ist.';
      if (canNavigate && leaderStep) {
        return (
          <Tooltip text={`${hint} Klicken, um Spieltagleiter zuzuweisen.`}>
            <button type={'button'} className={'processEventBadge isWarning'} onClick={() => onNavigateStep(leaderStep.key)}>
              <span className={'material-symbols-rounded processEventBadgeIcon'} aria-hidden={'true'}>warning</span>
              Leitung fehlt
            </button>
          </Tooltip>
        );
      }
      return (
        <Tooltip text={hint}>
          <span className={'processEventBadge isWarning'}>
            <span className={'material-symbols-rounded processEventBadgeIcon'} aria-hidden={'true'}>warning</span>
            Leitung fehlt
          </span>
        </Tooltip>
      );
    }
    return <span className={'processEventMuted'}>–</span>;
  };

  const renderCoverage = (evt) => {
    if (mailSteps.length === 0) return <span className={'processEventMuted'}>–</span>;
    return (
      <div className={'processEventCoverage'}>
        {mailSteps.map(step => {
          const covered = (evt.coveredStepIds ?? []).map(Number).includes(Number(step.stepId));
          const missing = !covered && (step.uncoveredEventIds ?? []).map(Number).includes(Number(evt.id));
          let cls = 'isPending';
          let text = `„${step.label}": noch nicht gesendet`;
          if (covered) {
            cls = 'isCovered';
            text = `„${step.label}": in der Mail enthalten`;
          } else if (missing) {
            cls = 'isMissing';
            text = `„${step.label}": nicht in der Mail enthalten – Nachtrag senden oder als erledigt markieren`;
          } else if (step.status === 'skipped') {
            text = `„${step.label}": übersprungen`;
          } else if (!isOpenEvent(evt)) {
            text = `„${step.label}": nicht gesendet`;
          }
          const icon = <span className={'material-symbols-rounded'} aria-hidden={'true'}>{stepIcon(step)}</span>;
          return (
            <Tooltip key={step.stepId} text={text}>
              {missing && canNavigate ? (
                <button
                  type={'button'}
                  className={`processCoverageIcon ${cls}`}
                  aria-label={text}
                  onClick={() => onNavigateStep(step.key)}
                >
                  {icon}
                </button>
              ) : (
                <span className={`processCoverageIcon ${cls}`} role={'img'} aria-label={text}>{icon}</span>
              )}
            </Tooltip>
          );
        })}
      </div>
    );
  };

  return (
    <div className={'processEventsSection'}>
      <div className={'processSectionHeader'}>
        <label className={'innerContainerLabel'}>{nouns.many} im Prozess</label>
        {!readOnly && <ActionButton icon={'add'} label={'Event'} type={3} onClick={onAdd} />}
      </div>

      {events.length === 0 ? (
        <p className={'processEmpty'}>Noch keine {nouns.many}. Füge bestehende hinzu oder lege neue an.</p>
      ) : (
        <div className={'processEventTableWrap'}>
          <div className={`processEventTable${readOnly ? ' isReadOnly' : ''}`} role={'table'} aria-label={`${nouns.many} im Prozess`}>
            <div className={'processEventTableRow processEventTableHead'} role={'row'}>
              <span role={'columnheader'}>Datum</span>
              <span role={'columnheader'} className={'processEventColStatus'}>Status</span>
              <span role={'columnheader'}>Freigegeben für</span>
              <span role={'columnheader'}>Spieltagleitung</span>
              <span role={'columnheader'}>Mails</span>
              <span role={'columnheader'} className={'processEventColCount'}>Anmeldungen</span>
              {!readOnly && <span role={'columnheader'} aria-label={'Aktionen'} />}
            </div>
            {events.map(e => (
              <div key={e.id} className={`processEventTableRow${isCancelledState(e.state) ? ' isCancelled' : ''}`} role={'row'}>
                <div className={'processEventCell processEventColDate'} role={'cell'}>
                  <span className={'processEventDatePrimary'}>{formatDate(e.date)}</span>
                  <span className={'processEventDateMeta'}>
                    {e.time && (
                      <span className={'processEventTime'}>
                        {formatTime(e.time)}{e.timeEnd ? `–${formatTime(e.timeEnd)}` : ''}
                      </span>
                    )}
                    {e.locationName && <span className={'processEventLocation'}>{e.locationName}</span>}
                  </span>
                </div>
                <div className={'processEventCell processEventColStatus'} role={'cell'}>
                  {/* event = the event's own rule flags neverPublic/linkSignup (exact stage set); releasedFor →
                      tooltip "Freigegeben für Mitglieder" as in the events area instead of "Member-Vorschau". */}
                  <EventStatus
                    statusId={e.state}
                    event={e}
                    releasedFor={releaseSummary((e.releasedParticipantGroupIds ?? []).map(id => ({ participantGroupId: id, released: true })), groupsById)}
                    single
                    compact
                  />
                </div>
                <div className={'processEventCell processEventColRelease'} role={'cell'}>
                  <span className={'processEventCellLabel'}>Freigegeben für</span>
                  {renderReleases(e)}
                </div>
                <div className={'processEventCell processEventColLeaders'} role={'cell'}>
                  <span className={'processEventCellLabel'}>Spieltagleitung</span>
                  {renderLeaders(e)}
                </div>
                <div className={'processEventCell processEventColMails'} role={'cell'}>
                  <span className={'processEventCellLabel'}>Mails</span>
                  {renderCoverage(e)}
                </div>
                <div className={'processEventCell processEventColCount'} role={'cell'}>
                  <Tooltip text={'Bestätigte Anmeldungen / max. Spieler'}>
                    <span className={'processEventCount'}>
                      <span className={'material-symbols-rounded processEventCountIcon'} aria-hidden={'true'}>group</span>{e.confirmedCount}/{e.maxPlayers}
                    </span>
                  </Tooltip>
                </div>
                {!readOnly && (
                  <div className={'processEventCell processEventColRemove'} role={'cell'}>
                    <Tooltip text={'Aus Prozess entfernen'}>
                      <button
                        type={'button'}
                        className={'processEventRemove'}
                        aria-label={`${formatDate(e.date)} aus Prozess entfernen`}
                        onClick={() => openRemove(e)}
                      >
                        <Icon icon={'close'} />
                      </button>
                    </Tooltip>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Popup
        name={REMOVE_POPUP}
        confirmText={'Entfernen'}
        headerText={`${nouns.one} entfernen`}
        type={'confirmaction'}
        onConfirm={handleRemove}
        withCancel
      >
        <p>
          <b>{eventShortLabel(removeEvent)}</b> wird aus diesem Prozess entfernt. Das Event selbst bleibt
          erhalten (Anmeldungen, Spieltagleitung und Mail-Verlauf unverändert).
        </p>
        {addOnNames && (
          <p className={'formFieldHint'}>
            Anmelde-Zusätze des Prozesses ({addOnNames}) sind für {nouns.one === 'Spieltag' ? 'den Spieltag' : 'das Event'} danach nicht mehr buchbar;
            bestehende Buchungen bleiben.
          </p>
        )}
        <CustomTogglebox
          placeholder={'Freigaben zurücknehmen'}
          checked={revokeOnRemove}
          onChange={ev => setRevokeOnRemove(ev.target.checked)}
        />
        <p className={'formFieldHint'}>
          {revokeOnRemove
            ? 'Die Freigaben aus diesem Prozess werden entfernt – das Event ist danach nur noch für Gruppen sichtbar, die im Bearbeiten-Popup manuell freigegeben sind.'
            : 'Die Freigaben bleiben bestehen und gelten danach als manuelle Freigaben (im Bearbeiten-Popup des Events änderbar).'}
        </p>
      </Popup>
    </div>
  );
}

export default ProcessEventTable;
